import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Loader from '../../components/Loader/Loader';
import './lockoutstatus.css';

const LockoutStatus = () => {
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [goToPage, setGoToPage] = useState('');
  const [tabsFilter, setTabsFilter] = useState(''); // 'Active', 'Inactive', or ''
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchLockoutData();
  }, []);

  const fetchLockoutData = async () => {
    setLoading(true);
    try {
      const response = await axios.get('https://alcobrakeadmin.com/v3/us/cadevices/getlockoutcycles');
      // Ensure data is an array
      setAllData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching lockout data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredData = allData.filter(item => {
    const matchesSearch = (item.state || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (item.statecode || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTab = tabsFilter === '' || item.status === tabsFilter;
    return matchesSearch && matchesTab;
  });

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const indexOfFirst = (currentPage - 1) * rowsPerPage;
  const currentItems = filteredData.slice(indexOfFirst, indexOfFirst + rowsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setGoToPage('');
    }
  };

  const handleGoToPage = (e) => {
    e.preventDefault();
    const pageNum = parseInt(goToPage);
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
      setCurrentPage(pageNum);
      setGoToPage('');
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let end = Math.min(totalPages, start + maxVisiblePages - 1);
    
    if (end - start + 1 < maxVisiblePages) {
      start = Math.max(1, end - maxVisiblePages + 1);
    }
    
    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push('...');
    }
    
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    
    if (end < totalPages) {
      if (end < totalPages - 1) pages.push('...');
      pages.push(totalPages);
    }
    
    return pages;
  };

  const getStatusClass = (status) => {
    return status === 'Active' ? 'status-assigned' : 'status-notassigned';
  };

  const handleEdit = (item) => {
    setEditingItem({ ...item });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setEditingItem(prev => ({
      ...prev,
      [name]: type === 'number' ? Number(value) : value
    }));
  };

  const handleSave = async () => {
    if (!editingItem) return;
    setSaving(true);
    try {
      const payload = {
        id: Number(editingItem.id),
        state: editingItem.state?.trim() || '',
        statecode: editingItem.statecode?.trim() || '',
        status: editingItem.status || '',
        fail_threshold: Number(editingItem.fail_threshold),
        fail_limit_value: String(editingItem.fail_limit_value),
        fail_lockout_delay_days: Number(editingItem.fail_lockout_delay_days),
        fail_interval_mins: Number(editingItem.fail_interval_mins),
        refused_threshold: Number(editingItem.refused_threshold),
        refused_lockout_delay_days: Number(editingItem.refused_lockout_delay_days),
        insufficient_threshold: Number(editingItem.insufficient_threshold),
        insufficient_lockout_delay_days: Number(editingItem.insufficient_lockout_delay_days),
        sampler_disconnect_threshold: Number(editingItem.sampler_disconnect_threshold),
        sampler_lockout_delay_days: Number(editingItem.sampler_lockout_delay_days),
        battery_tamper_threshold: Number(editingItem.battery_tamper_threshold),
        battery_tamper_lockout_delay_days: Number(editingItem.battery_tamper_lockout_delay_days),
        violation_counts: Number(editingItem.violation_counts),
        violation_days_interval: Number(editingItem.violation_days_interval),
        violation_lockout_delay_days: Number(editingItem.violation_lockout_delay_days),
      };

      await axios.post('https://alcobrakeadmin.com/v3/us/cadevices/updatecycle', payload);
      
      // Update local state
      setAllData(prev => prev.map(item => item.id === editingItem.id ? { ...editingItem } : item));
      closeModal();
    } catch (error) {
      console.error('Error saving lockout configuration:', error);
      alert('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="lockout-app">
      <div className="lockout-card">
        {/* Header */}
        <div className="lockout-header">
          <div>
            <h1 className="lockout-title">Lockout Status</h1>
            <p className="lockout-subtitle">Manage state-wise lockout configuration and thresholds</p>
          </div>
          <div className="tabs-filter-buttons">
            <button 
              className={`filter-btn ${tabsFilter === 'Active' ? 'active green' : ''}`}
              onClick={() => setTabsFilter(tabsFilter === 'Active' ? '' : 'Active')}
            >
              Active
            </button>
            <button 
              className={`filter-btn ${tabsFilter === 'Inactive' ? 'active red' : ''}`}
              onClick={() => setTabsFilter(tabsFilter === 'Inactive' ? '' : 'Inactive')}
            >
              Inactive
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="lockout-filters">
          <div className="search-wrapper">
            <svg className="search-svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
              <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M12.5 12.5L16 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <input
              type="text"
              className="search-input"
              placeholder="Search by State or Code..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            />
            {searchTerm && (
              <button className="clear-btn" onClick={() => setSearchTerm('')}>×</button>
            )}
          </div>
          <div className="records-info">
            <span className="records-label">Show</span>
            <select 
              className="records-select"
              value={rowsPerPage} 
              onChange={e => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
            >
              {[15, 25, 50, 100].map(n => <option key={n}>{n}</option>)}
            </select>
            <span className="records-label">entries</span>
          </div>
        </div>

        {/* Table */}
        <div className="lockout-table-container">
          {loading ? (
            <div className="table-loader-container">
              <Loader />
              <p>Fetching lockout data...</p>
            </div>
          ) : (
            <table className="lockout-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>State</th>
                  <th>Code</th>
                  <th>Fail Threshold</th>
                  <th>Fail Limit Value</th>
                  <th>Fail Lockout Days</th>
                  <th>Fail Interval Mins</th>
                  <th>Refused Threshold</th>
                  <th>Refused Lockout Days</th>
                  <th>Insufficient Threshold</th>
                  <th>Insufficient Lockout Days</th>
                  <th>Sampler Disconnect Threshold</th>
                  <th>Sampler Lockout Days</th>
                  <th>Battery Tamper Threshold</th>
                  <th>Battery Tamper Lockout Days</th>
                  <th>Violation Counts</th>
                  <th>Violation Days Interval</th>
                  <th>Violation Lockout Days</th>
                  <th className="text-center">Status</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map((item) => (
                    <tr key={item.id}>
                      <td className="id-cell">{item.id}</td>
                      <td className="state-cell">{item.state}</td>
                      <td className="code-cell">{item.statecode}</td>
                      <td className="number-cell">{item.fail_threshold}</td>
                      <td className="value-cell">{item.fail_limit_value}</td>
                      <td className="number-cell">{item.fail_lockout_delay_days}</td>
                      <td className="number-cell">{item.fail_interval_mins}</td>
                      <td className="number-cell">{item.refused_threshold}</td>
                      <td className="number-cell">{item.refused_lockout_delay_days}</td>
                      <td className="number-cell">{item.insufficient_threshold}</td>
                      <td className="number-cell">{item.insufficient_lockout_delay_days}</td>
                      <td className="number-cell">{item.sampler_disconnect_threshold}</td>
                      <td className="number-cell">{item.sampler_lockout_delay_days}</td>
                      <td className="number-cell">{item.battery_tamper_threshold}</td>
                      <td className="number-cell">{item.battery_tamper_lockout_delay_days}</td>
                      <td className="number-cell">{item.violation_counts}</td>
                      <td className="number-cell">{item.violation_days_interval}</td>
                      <td className="number-cell">{item.violation_lockout_delay_days}</td>
                      <td className="status-cell">
                        <span className={`status-badge ${getStatusClass(item.status)}`}>
                          {item.status}
                        </span>
                      </td>
                      <td className="actions-cell text-center">
                        <div className="action-buttons">
                          <button 
                            className="action-icon edit-btn"
                            onClick={() => handleEdit(item)}
                            title="Edit"
                          >
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                              <path d="M10 1L13 4L4.5 12.5H1V9L10 1Z" stroke="currentColor" strokeWidth="1.2"/>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="20" className="text-center no-data-cell">No data found</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {indexOfFirst + 1} to {Math.min(indexOfFirst + rowsPerPage, filteredData.length)} of {filteredData.length} entries
          </div>
          <div className="pagination-controls">
            <button 
              className="page-btn" 
              onClick={() => handlePageChange(1)} 
              disabled={currentPage === 1}
            >
              «
            </button>
            <button 
              className="page-btn" 
              onClick={() => handlePageChange(currentPage - 1)} 
              disabled={currentPage === 1}
            >
              ‹
            </button>
            {getPageNumbers().map((p, i) => (
              p === '...' 
                ? <span key={i} className="page-dots">…</span>
                : (
                  <button 
                    key={p} 
                    className={`page-btn ${currentPage === p ? 'active' : ''}`}
                    onClick={() => handlePageChange(p)}
                  >
                    {p}
                  </button>
                )
            ))}
            <button 
              className="page-btn" 
              onClick={() => handlePageChange(currentPage + 1)} 
              disabled={currentPage === totalPages}
            >
              ›
            </button>
            <button 
              className="page-btn" 
              onClick={() => handlePageChange(totalPages)} 
              disabled={currentPage === totalPages}
            >
              »
            </button>
          </div>
          <div className="goto-wrapper">
            <span>Go to</span>
            <form onSubmit={handleGoToPage}>
              <input
                type="number"
                min="1"
                max={totalPages}
                value={goToPage}
                onChange={e => setGoToPage(e.target.value)}
                placeholder="page"
              />
              <button type="submit">Go</button>
            </form>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {isModalOpen && editingItem && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-card" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Lockout Configurations</h3>
              <button className="modal-close-btn" onClick={closeModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="modal-grid">
                <div className="input-group">
                  <label>State</label>
                  <input name="state" value={editingItem.state} onChange={handleInputChange} />
                </div>
                <div className="input-group">
                  <label>State Code</label>
                  <input name="statecode" value={editingItem.statecode} onChange={handleInputChange} />
                </div>
                <div className="input-group">
                  <label>Fail Threshold</label>
                  <input type="number" name="fail_threshold" value={editingItem.fail_threshold} onChange={handleInputChange} />
                </div>
                <div className="input-group">
                  <label>Fail Limit Value</label>
                  <input name="fail_limit_value" value={editingItem.fail_limit_value} onChange={handleInputChange} />
                </div>
                <div className="input-group">
                  <label>Fail Lockout Days</label>
                  <input type="number" name="fail_lockout_delay_days" value={editingItem.fail_lockout_delay_days} onChange={handleInputChange} />
                </div>
                <div className="input-group">
                  <label>Fail Interval Minutes</label>
                  <input type="number" name="fail_interval_mins" value={editingItem.fail_interval_mins} onChange={handleInputChange} />
                </div>
                <div className="input-group">
                  <label>Refused Threshold</label>
                  <input type="number" name="refused_threshold" value={editingItem.refused_threshold} onChange={handleInputChange} />
                </div>
                <div className="input-group">
                  <label>Refused Lockout Days</label>
                  <input type="number" name="refused_lockout_delay_days" value={editingItem.refused_lockout_delay_days} onChange={handleInputChange} />
                </div>
                <div className="input-group">
                  <label>Insufficient Threshold</label>
                  <input type="number" name="insufficient_threshold" value={editingItem.insufficient_threshold} onChange={handleInputChange} />
                </div>
                <div className="input-group">
                  <label>Insufficient Lockout Days</label>
                  <input type="number" name="insufficient_lockout_delay_days" value={editingItem.insufficient_lockout_delay_days} onChange={handleInputChange} />
                </div>
                <div className="input-group">
                  <label>Sampler Disconnect Threshold</label>
                  <input type="number" name="sampler_disconnect_threshold" value={editingItem.sampler_disconnect_threshold} onChange={handleInputChange} />
                </div>
                <div className="input-group">
                  <label>Sampler Lockout Days</label>
                  <input type="number" name="sampler_lockout_delay_days" value={editingItem.sampler_lockout_delay_days} onChange={handleInputChange} />
                </div>
                <div className="input-group">
                  <label>Battery Tamper Threshold</label>
                  <input type="number" name="battery_tamper_threshold" value={editingItem.battery_tamper_threshold} onChange={handleInputChange} />
                </div>
                <div className="input-group">
                  <label>Battery Tamper Lockout Days</label>
                  <input type="number" name="battery_tamper_lockout_delay_days" value={editingItem.battery_tamper_lockout_delay_days} onChange={handleInputChange} />
                </div>
                <div className="input-group">
                  <label>Violation Counts</label>
                  <input type="number" name="violation_counts" value={editingItem.violation_counts} onChange={handleInputChange} />
                </div>
                <div className="input-group">
                  <label>Violation Days Interval</label>
                  <input type="number" name="violation_days_interval" value={editingItem.violation_days_interval} onChange={handleInputChange} />
                </div>
                <div className="input-group">
                  <label>Violation Lockout Days</label>
                  <input type="number" name="violation_lockout_delay_days" value={editingItem.violation_lockout_delay_days} onChange={handleInputChange} />
                </div>
                <div className="input-group">
                  <label>Status</label>
                  <select name="status" value={editingItem.status} onChange={handleInputChange}>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={closeModal} disabled={saving}>Cancel</button>
              <button className="save-btn" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LockoutStatus;
